import { useEffect, useMemo, useState } from "react";

import { useForm } from "react-hook-form";

import BasketTable from "../../components/basket/BasketTable";
import ConfirmModal from "../../components/shared/Confirm/ConfirmModal";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import SelectInput from "../../components/shared/Inputs/SelectInput";
import FullscreenLoading from "../../components/shared/LoadingWheelFullscreen";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useBaskets from "../../data/useBaskets";
import classNames from "../../utils/classNames";
import PriceFormat from "../../utils/priceFormat";
import { TextInput } from "../../components/shared/Inputs/TextInput";
import PickAddress from "../../components/shared/Addresses/PickAddress";
import { AccountCombobox } from "../../components/accounts/AccountCombobox";
import { IAccount } from "../../models/account/IAccount";
import useNotification from "../../components/notifications/useNotifications";
import useShippingPolicies from "../../data/useShippingPolicies";
import { IShippingPolicy } from "../../models/ShippingPolicy";
import BasketInvoiceInputs from "./BasketInvoiceInputs";
import { FormatType } from "../../models/EbayOffer";


export default function Basket() {
  const { setInfo } = usePageNameContext();
  const { addNotification } = useNotification();
  const { register, setValue, getValues, watch, reset } = useForm({
    defaultValues: {
      paymentMethod: 1,
      accountId: 0,
      shippingPolicyId: "0",
      notes: "",
      address: {
        line1: "",
        line2: "",
        line3: "",
        line4: "",
        locality: "",
        town: "",
        county: "",
      }
    }
  });
  const { getBasket, clearBasket, checkout } = useBaskets("Basket");
  const { shippingPolicies: allShippingPolicies } = useShippingPolicies();
  const [totalPrice, setTotalPrice] = useState(0);
  const [selectedAccount, setSelectedAccount] = useState<IAccount | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  // const [shippingPolicies, setShippingPolicies] = useState<Record<string, number | string>[]>([]);


  // Don't need now it's a drawer - if we ever use this as a page too then we can add in conditionally
  // useEffect(() => {
  //   setInfo({
  //     name: "Basket",
  //     desc: "View and manage your current basket",
  //   });
  // }, [])

  useEffect(() => {
    if (!getBasket.isLoading && !allShippingPolicies.isLoading) {

      if (getBasket.data?.data.items) {

        const totalPrice = getBasket.data.data.items.reduce((acc, item) => {
          // Use basket price if set, otherwise calculate default price
          if (item.basketPrice !== undefined && item.basketPrice !== null) {
            return acc + item.basketPrice;
          }
          
          // Fall back to calculating default price
          if (item.part.offers && item.part.offers.length > 0) {
            const offer = item.part.offers[0];
            if (offer.format === FormatType.FixedPrice) {
              return acc + parseFloat(offer.buyItNowPrice?.toString() || "0");
            } else {
              return acc + parseFloat(offer.auctionStartPrice?.toString() || "0");
            }
          } else {
            return acc + parseFloat(item.part.enteredPrice?.toString() || "0");
          }
        }, 0);

        setTotalPrice(totalPrice);
      } else {
        setTotalPrice(0);
      }

      setIsLoading(false);
    }
  }, [getBasket.isLoading, getBasket.data, allShippingPolicies.isLoading, allShippingPolicies.data]);

  const handleSubmit = () => {

    if (!selectedAccount) {
      addNotification({
        primaryText: "No Account Selected",
        variant: "warn"
      });
      return;
    }

    const data = getValues();

    if (data.shippingPolicyId === undefined || data.shippingPolicyId === "0") {
      addNotification({
        primaryText: "No Shipping Policy Selected",
        variant: "warn"
      });
      return;
    }

    setIsSubmitLoading(true);

    const body = {
      accountId: selectedAccount.id,
      paymentMethod: data.paymentMethod,
      shippingPolicyId: data.shippingPolicyId,
      notes: data.notes,
      ...data.address,
    }

    checkout.mutate(body, {
      onSuccess: (file) => {
        // console.log("Invoice received:", file);
        const url = window.URL.createObjectURL(file);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `invoice-${getBasket.data?.data.id}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        reset();
      },
      onSettled: () => {
        setIsSubmitLoading(false);
        setIsConfirmOpen(false);
      }
    });
  };

  const isEmpty = useMemo(() => {
    return getBasket.data?.data.items && getBasket.data?.data.items.length === 0;
  }, [getBasket.data?.data.items]);

  if (isLoading) {
    return <FullscreenLoading />;
  }

  const getShippingPrice = (shippingPolicyId: string) => {
    const policy = allShippingPolicies.data?.data.find((p: IShippingPolicy) => p.id === shippingPolicyId);

    if (!policy) {
      console.warn("Shipping policy not found for ID:", shippingPolicyId);
    }

    return policy ? policy.price : 0;
  }

  return (
    <>
      <ConfirmModal open={isConfirmOpen} setOpen={setIsConfirmOpen}
        title={"Are you sure you want sell these items?"}
        message={`Parts will be withdrawn from eBay and marked as sold and money received.`}
        confirmButtonText={"Submit"}
        onConfirm={handleSubmit}
        isLoading={isSubmitLoading}
      />
      <form className="flex flex-col w-full gap-4">
        {isEmpty ? (
          <p className="mt-8 text-center text-xl text-gray-700">Your basket is empty</p>
        ) :
          <BasketTable data={getBasket.data?.data.items ?? []} />
        }

        <BasketInvoiceInputs
          register={register}
          setValue={setValue}
          watch={watch}
          setSelectedAccount={setSelectedAccount}
        />

        <div>
          <p className="text-right text-xl text-gray-700">Total: {getBasket.data?.data.items?.length} items</p>
          <p className="text-right text-xl text-gray-700">Subtotal: {PriceFormat(totalPrice)}</p>
          <p className="text-right text-xl text-gray-700">Shipping: {PriceFormat(getShippingPrice(watch("shippingPolicyId")))}</p>
          <p className="text-right text-xl text-gray-700">Total Price: {PriceFormat(totalPrice + getShippingPrice(watch("shippingPolicyId")))}</p>
        </div>

        <div className="flex justify-between mt-4">
          <ButtonInput label="Clear" classes={classNames(isEmpty ? "" : "!bg-red-600 hover:!bg-red-700")} onClick={() => clearBasket.mutate()} disabled={isEmpty} />
          <ButtonInput label="Submit" classes={undefined} onClick={() => setIsConfirmOpen(true)} disabled={isEmpty} />
        </div>
      </form>
    </>
  );
}